// NIFC dispatch-zone boundaries layer family.
//
// Polygon fills (very low alpha) + dashed outlines. Labels show GACC
// abbreviation + dispatch unit so pilots can identify the zone they're
// flying over. The source id is `'dispatch-zones'` (hyphenated) — this is
// intentional and byte-identical to the original addOverlays.ts form.
//
// Store key is `layers.dispatch` (NOT `layers['dispatch-zones']`) — matching
// the uiStore shape exactly as it existed before extraction.
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 3 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids,
// minzoom, and layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.
// The boundary-crossing alert wired separately via useAlertTriggers/
// dispatch watcher is NOT touched here — applyState only handles
// visibility re-application.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addDispatchZonesLayers(map: MlMap): void {
  if (!map.getSource('dispatch-zones')) {
    map.addSource('dispatch-zones', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'dispatch-fill',
      type: 'fill',
      source: 'dispatch-zones',
      layout: { visibility: 'none' },
      paint: {
        'fill-color': '#84cc16',         // lime green — NIFC convention
        'fill-opacity': 0.16,
        'fill-outline-color': 'rgba(0,0,0,0)', // outlines drawn by the line layer
      },
    });
    // Dark halo line first — gives the dashed lime line a contrasting
    // backing so the boundary stays legible on tan sectional terrain.
    map.addLayer({
      id: 'dispatch-line-halo',
      type: 'line',
      source: 'dispatch-zones',
      layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#0b0d0f',
        'line-width': [
          'interpolate', ['linear'], ['zoom'],
          4, 3.2,
          8, 4.4,
          12, 6.0,
        ],
        'line-opacity': 0.65,
      },
    });
    map.addLayer({
      id: 'dispatch-line',
      type: 'line',
      source: 'dispatch-zones',
      layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#bef264',         // brighter lime so it pops vs chart
        'line-width': [
          'interpolate', ['linear'], ['zoom'],
          4, 1.8,
          8, 2.6,
          12, 3.6,
        ],
        'line-dasharray': [3, 2],
        'line-opacity': 1,
      },
    });
    map.addLayer({
      id: 'dispatch-label',
      type: 'symbol',
      source: 'dispatch-zones',
      layout: {
        visibility: 'none',
        'text-field': [
          'format',
          ['coalesce', ['get', 'GACCAbbreviation'], ''],
          { 'font-scale': 0.9 },
          '\n',
          {},
          ['coalesce', ['get', 'DispUnitID'], ['get', 'DispName'], ''],
          { 'font-scale': 1.0 },
        ],
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          5, 9, 8, 11, 11, 13,
        ],
        'text-anchor': 'center',
        'symbol-placement': 'point',
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-padding': 4,
      },
      paint: {
        'text-color': '#bef264',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.3,
      },
      minzoom: 5,
    });
  }
}

registerLayerFamily({
  id: 'dispatch',
  label: 'NIFC Dispatch Zones',
  sourceId: 'dispatch-zones',
  layerIds: ['dispatch-fill', 'dispatch-line-halo', 'dispatch-line', 'dispatch-label'],
  mount: (map) => addDispatchZonesLayers(map),
  visibilitySelector: (l) => l.dispatch,
  applyState: (map) => {
    if (!map.getLayer('dispatch-fill')) return;
    const show = useLayerVisibilityStore.getState().layers.dispatch;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('dispatch-fill', 'visibility', v);
    map.setLayoutProperty('dispatch-line-halo', 'visibility', v);
    map.setLayoutProperty('dispatch-line', 'visibility', v);
    map.setLayoutProperty('dispatch-label', 'visibility', v);
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('dispatch');
    if (!spec) return;
    markOverlayLoaded('dispatch', true);
    return refreshOverlay(map, spec);
  },
});
